import type { ThreadKeyToCommentIdMap, UnreadComment } from '../types/unreadCommentsTypes';

import { CommentType } from './unreadCommentCountUtils';

export enum PageMode {
	EDITOR,
	LIVE,
	RENDERER,
}

export type UnreadPubSubParams = {
	inlineCommentRequestBody: {
		commentId?: string;
		markerRef?: string;
	};
	readCommentsListState: Set<string>;
	isNotCurrentUser: boolean;
	updateUnreadCommentsListState: (
		transform: (
			prevUnreadCommentsListState: UnreadComment[],
			threadKeyToCommentIdMap: ThreadKeyToCommentIdMap,
		) => UnreadComment[],
	) => void;
	threadKeyToCommentIdMap: ThreadKeyToCommentIdMap;
	isTopLevelComment: boolean;
	pageMode?: PageMode;
};

// add to the unread list state for new, reattached, unresolved inline comment, only if it's unread
// this applies to only collaborators because for the current user, the comment box will be open on unresolve
export const handleAddedInlineCommentForUnread = ({
	inlineCommentRequestBody,
	readCommentsListState,
	isNotCurrentUser,
	updateUnreadCommentsListState,
	isTopLevelComment,
	pageMode,
}: UnreadPubSubParams): void => {
	const { commentId, markerRef: threadKey } = inlineCommentRequestBody;

	if (isNotCurrentUser && commentId && threadKey) {
		// this could be top level comment or reply
		const unreadCommentsToAdd: UnreadComment[] = !readCommentsListState.has(commentId)
			? [
					{
						id: commentId,
						threadKey,
						commentType: CommentType.INLINE,
					},
				]
			: [];

		//if its a reply and the comment thread is open, dont increment the count
		const isThreadKeyOpen =
			pageMode === PageMode.EDITOR || pageMode === PageMode.LIVE
				? document.querySelector(`[id="${threadKey}"] .ak-editor-annotation-focus.true`)
				: document.querySelector(`[id="${threadKey}"][data-has-focus="true"]`);
		if (!isTopLevelComment && isThreadKeyOpen) {
			return;
		}

		updateUnreadCommentsListState((prevUnreadCommentsListState, threadKeyToCommentIdMap) => {
			// for reattach / unresolve: if the comment is a top level comment, then add the replies, if not, don't need to add anything else
			if (isTopLevelComment && threadKeyToCommentIdMap[threadKey]) {
				for (const reply of threadKeyToCommentIdMap[threadKey]) {
					if (!readCommentsListState.has(reply)) {
						unreadCommentsToAdd.push({
							id: reply,
							threadKey,
							commentType: CommentType.INLINE,
						});
					}
				}
			}

			return [...prevUnreadCommentsListState, ...unreadCommentsToAdd];
		});
	}
};

// remove from the unread list state for resolved or deleted inline comment if it's unread
// the comment is already considered read for yourself when you resolve, so we only need to check for collaborators
export const handleObliteratedInlineCommentForUnread = ({
	inlineCommentRequestBody,
	readCommentsListState,
	isNotCurrentUser,
	updateUnreadCommentsListState,
	isTopLevelComment,
}: UnreadPubSubParams): void => {
	const { commentId, markerRef } = inlineCommentRequestBody;
	if (isNotCurrentUser && commentId && markerRef && !readCommentsListState.has(commentId)) {
		const unreadCommentsToObliterate: Set<string> = new Set([commentId]);

		updateUnreadCommentsListState((prevUnreadCommentsListState, markerRefToCommentIdMap) => {
			// for resolve / delete: if the comment is a top level comment, then remove the replies as well
			if (isTopLevelComment && markerRefToCommentIdMap[markerRef]) {
				for (const reply of markerRefToCommentIdMap[markerRef]) {
					unreadCommentsToObliterate.add(reply);
				}
			}

			return prevUnreadCommentsListState.filter(
				(comment) => !unreadCommentsToObliterate.has(comment.id),
			);
		});
	}
};
